import React from 'react';
import i18next from 'i18next';
import Spinner from 'react-spinkit';
import { connect } from 'react-redux';
import { Scrollbars } from 'react-custom-scrollbars';
import { createSelector } from 'reselect';

import apiForm from '../../Form/apiForm';
import ECouponBet from '../ECouponBet/ECouponBet';
import classNames from 'classnames';
import Icon from '../../Icon/Icon';
import { isNumeric, numberWithSpaces, plural } from '../../../utils';
import InputTextIcon from '../../InputTextIcon/InputTextIcon';

import Event from '../Event/Event';
import { setNewBetsFlag } from '../../../actions/appActions';

import trashIcon from 'icons/trash.svg';
import polygonIcon from 'icons/polygon.svg';
import coinIcon from 'icons/coin.svg';
import EApi from '../../../api/esports';
import Errors from './Errors';
import ButtonBorder from '../../Button/ButtonBorder';
import couponManager from '../../../couponManager';
import makeCancelable from '../../../api/cancelable';
import EButton from '../EButton/EButton';
import ECheckbox from '../ECheckbox/ECheckbox';

const MIN_BET = 1;
const DEFAULT_MAX_BET = 50000;

const SHOW_ERRORS_TIMEOUT = 3000;

const STATUS_WAITING = 0;
const STATUS_PLACED = 1;
const STATUS_ERROR = 2;

class ECoupon extends React.PureComponent {
  constructor(props) {
    super(props);

    this.state = {
      sum: 100,
      changedBets: [],
      maxBet: 0,
      totalCf: 1,
      agreeChangedCf: localStorage.getItem('agreeChangedCf') === 'true',
    };

    this.waitingTimer = null;
    this.errorsTimer = null;
    this.placedTimer = null;

    this.orderWaiting = this.orderWaiting.bind(this);

    this.requests = [];
  }

  componentWillUnmount() {
    this.requests.forEach((cancel) => cancel());
    clearTimeout(this.waitingTimer);
    clearTimeout(this.errorsTimer);
    clearTimeout(this.placedTimer);
  }

  componentDidUpdate(prevProps) {
    if (this.props.bets.length !== prevProps.bets.length) {
      this.props.clearErrors();

      this.setState(
        {
          changedBets: [],
          ...this.recalcParams(),
        },
        () => {
          this.updateSum(this.state.sum);
        }
      );
    }
  }

  componentDidMount() {
    this.setState(this.recalcParams());
  }

  recalcParams() {
    const totalCf = this.props.bets.reduce((acc, bet) => acc * bet.cf, 1);
    let maxBet = Math.floor(
      Math.min(
        ...this.props.bets.map((bet) => {
          return typeof bet['maxbet'] !== 'undefined'
            ? bet['maxbet']
            : DEFAULT_MAX_BET;
        })
      ) / totalCf
    );

    if (maxBet === Infinity) {
      maxBet = DEFAULT_MAX_BET;
    }

    return {
      maxBet,
      totalCf,
    };
  }

  toggleCoupon = () => {
    const {
      couponState: { open },
    } = this.props;

    if (open) {
      couponManager.closeCoupon();
    } else {
      couponManager.openCoupon();
    }
  };

  updateSum(value, validate = true) {
    if (validate) {
      if (!isNumeric(value)) value = MIN_BET;
      value = Math.min(
        this.props.balance,
        this.state.maxBet,
        Math.max(MIN_BET, value)
      );
    }

    this.setState({ sum: value });
  }

  removeAll = () => {
    if (this.props.couponState.waiting) {
      return false;
    }

    this.setState({ changedBets: [] });

    couponManager.removeAll();
    this.props.clearErrors();
  };

  setMaxSum = () => {
    this.setState({ sum: this.state.maxBet });
  };

  orderWaiting(orderId) {
    const { promise, cancel } = makeCancelable(EApi.orderStatus(orderId));
    this.requests.push(cancel);

    promise.then((response) => {
      if (response.status === 'success') {
        const orderStatus = response.data.status;

        if (orderStatus === STATUS_WAITING) {
          return setTimeout(this.orderWaiting.bind(this, orderId), 500);
        }

        if (orderStatus === STATUS_ERROR) {
          this.props.pushError(response.data.mes);
          couponManager.setCouponState({ waiting: false, placed: false });

          clearTimeout(this.errorsTimer);
          this.errorsTimer = setTimeout(() => {
            this.props.clearErrors();
          }, SHOW_ERRORS_TIMEOUT);
        }

        if (orderStatus === STATUS_PLACED) {
          couponManager.setCouponState({ waiting: false, placed: true });

          this.placedTimer = setTimeout(this.onPlaceAccept, 5000);
          this.props.setNewBetsFlag(true);
        }
      }
    });
  }

  onRemove = (marketId, betId) => {
    if (this.props.couponState.waiting) {
      return false;
    }

    this.setState({
      changedBets: this.state.changedBets.filter((id) => id !== betId),
    });

    couponManager.removeBet(marketId, betId);
  };

  onBetChange = (betId) => {
    if (this.state.agreeChangedCf) return;

    this.setState((prevState) => ({
      changedBets: prevState.changedBets.concat(betId),
    }));
  };

  onBetCfChange = (betId) => {
    this.setState(this.recalcParams());
  };

  onChangeAccept = () => {
    this.setState({ changedBets: [] });
  };

  onPlaceAccept = () => {
    clearTimeout(this.placedTimer);
    couponManager.removeAll();
    couponManager.setCouponState({ placed: false });
  };

  onSumChange = (e) => {
    this.updateSum(e.currentTarget.value, false);
  };

  onSumBlur = (e) => {
    this.updateSum(e.currentTarget.value);
  };

  onSubmit = (e) => {
    e.preventDefault();
    this.props.beforeSubmit();

    const { promise, cancel } = makeCancelable(
      EApi.openOrder(this.state.sum, this.props.bets)
    );
    this.requests.push(cancel);

    promise.then((response) => {
      this.props.afterSubmit(response);
      if (response.status === 'success') {
        couponManager.setCouponState({ waiting: true });
        this.orderWaiting(response.data.order_id);
      } else {
        couponManager.setCouponState({ waiting: false, placed: false });
        clearTimeout(this.errorsTimer);
        this.errorsTimer = setTimeout(() => {
          this.props.clearErrors();
        }, SHOW_ERRORS_TIMEOUT);
      }
    });
  };

  onAgreeChanged = () => {
    this.setState(
      {
        agreeChangedCf: !this.state.agreeChangedCf,
      },
      () => {
        localStorage.setItem('agreeChangedCf', this.state.agreeChangedCf);
      }
    );
  };

  render() {
    const {
      formState: { pending, errors, invalid },
      bets,
      couponState,
      signedIn,
      managerErrors,
    } = this.props;
    const allErrors = [...errors, ...managerErrors];
    let { sum, changedBets, totalCf, maxBet } = this.state;
    const count = bets.length;

    if (couponState.placed) {
      changedBets = [];
    }

    return (
      <div className={classNames('e-coupon', { open: couponState.open })}>
        <form onSubmit={this.onSubmit}>
          <div className="e-coupon__header">
            <span>{i18next.t('ecoupon.title')}</span>
            <div className="e-coupon__header-actions">
              <span className="e-coupon__count">
                {i18next.t('ecoupon.count', { count })}
              </span>
              <div className="e-coupon__round-button" onClick={this.removeAll}>
                <Icon glyph={trashIcon} />
              </div>
              <div
                className="e-coupon__round-button e-coupon__toggler"
                onClick={this.toggleCoupon}
              >
                <Icon glyph={polygonIcon} />
              </div>
            </div>
          </div>

          {signedIn ? (
            <div className="e-coupon__full">
              {(couponState.waiting || pending) && (
                <div className="e-coupon__waiting">
                  <Spinner
                    name="ball-clip-rotate"
                    color="white"
                    fadeIn="quarter"
                  />
                </div>
              )}

              {couponState.placed && (
                <div className="e-coupon__placed">
                  <div className="e-coupon__placed-title">
                    {i18next.t('ecoupon.accepted')}
                  </div>
                  <div className="e-coupon__placed-buttons">
                    <ButtonBorder onClick={this.onPlaceAccept}>
                      {i18next.t('ecoupon.ok')}
                    </ButtonBorder>
                  </div>
                </div>
              )}

              {changedBets.length > 0 && (
                <div className="e-coupon__accept">
                  <div className="e-coupon__accept-title">
                    {i18next.t('ecoupon.changed_title')}
                  </div>
                  <div className="e-coupon__accept-text">
                    <span>{i18next.t('ecoupon.changed_text')}</span>
                    <span>{i18next.t('ecoupon.changed_question')}</span>
                  </div>
                  <div className="e-coupon__accept-buttons">
                    <ButtonBorder onClick={() => {}}>
                      {i18next.t('ecoupon.changed_no')}
                    </ButtonBorder>
                    <ButtonBorder onClick={this.onChangeAccept}>
                      {i18next.t('ecoupon.changed_accept')}
                    </ButtonBorder>
                  </div>
                </div>
              )}

              <Scrollbars
                style={{ height: '100%' }}
                hideTracksWhenNotNeeded={true}
              >
                <div className="e-coupon__bets">
                  {bets.map((bet) => (
                    <Event eventId={bet['event_id']} key={bet['bet_id']}>
                      {(event) => (
                        <ECouponBet
                          color={
                            couponState.waiting
                              ? 'blue'
                              : errors && errors.length
                              ? 'red'
                              : 'yellow'
                          }
                          betName={bet['full_name'] || bet['name']}
                          title={bet['market']}
                          cf={bet['cf']}
                          eventName={`${event['name_ht']} vs ${event['name_at']}`}
                          isLive={event['live'] === 1}
                          marketId={bet['market_id']}
                          betId={bet['bet_id']}
                          accepted={
                            this.state.changedBets.includes(bet['bet_id']) ===
                            false
                          }
                          onRemove={this.onRemove}
                          onBetChange={this.onBetChange}
                          onBetCfChange={this.onBetCfChange}
                          waiting={this.props.couponState.waiting}
                        />
                      )}
                    </Event>
                  ))}
                </div>
              </Scrollbars>

              <Errors errors={allErrors} />

              <div className="e-coupon__info">
                <div>
                  <span className="e-coupon__label">
                    {i18next.t('ecoupon.sum')}
                  </span>
                  <InputTextIcon
                    onBlur={this.onSumBlur}
                    onChange={this.onSumChange}
                    value={sum}
                    wrapperClassName="e-coupon__sum-input"
                    className={classNames({
                      invalid: invalid.includes('summ'),
                    })}
                    glyph={coinIcon}
                    position="right"
                    maxLength={6}
                  />
                </div>

                <div>
                  <span className="e-coupon__bet-type">
                    {count > 1 ? `Express: ${count}` : 'Single'}
                  </span>

                  <span className="e-coupon__total-cf">
                    x &nbsp;{totalCf.toFixed(2)}
                  </span>

                  <div
                    className="e-coupon__max text-icon"
                    onClick={this.setMaxSum}
                  >
                    Max:&nbsp;<span>{numberWithSpaces(maxBet)} </span>
                    <Icon glyph={coinIcon} className="text-icon__icon" />
                  </div>
                </div>
              </div>

              <div className="e-coupon__settings">
                <ECheckbox
                  onChange={this.onAgreeChanged}
                  checked={this.state.agreeChangedCf}
                  className="e-coupon__changed-cf"
                >
                  {i18next.t('ecoupon.accept_current_cf')}
                </ECheckbox>
              </div>

              <div className="e-coupon__bottom">
                <div>
                  <span className="e-coupon__win-label">
                    {i18next.t('ecoupon.win')}:
                  </span>
                  <span className="e-coupon__total-win text-icon">
                    <Icon glyph={coinIcon} className="text-icon__icon" />
                    <span>{numberWithSpaces(Math.floor(sum * totalCf))} </span>
                  </span>
                </div>
                <EButton
                  className={classNames('e-coupon__stake-button', {
                    active: pending || couponState.waiting,
                  })}
                  disabled={pending || this.state.changedBets.length}
                >
                  <span>{i18next.t('ecoupon.bet')}:</span>
                </EButton>
              </div>
            </div>
          ) : (
            <div className="e-coupon__auth">{i18next.t('ecoupon.auth')}</div>
          )}
        </form>
      </div>
    );
  }
}

const getBets = createSelector(
  (state) => state.coupon.bets,
  (state) => state.markets.dict,
  (betKeys, dict) =>
    betKeys.reduce((acc, betKey) => {
      const [marketId, betId] = ('' + betKey).split('@');

      try {
        const bet = dict[marketId].bets[betId];
        acc.push(bet);
      } catch (e) {}

      return acc;
    }, [])
);

export default connect(
  (state) => {
    return {
      signedIn: !!state.user.profile,
      balance: state.user.balance,
      couponState: state.coupon.couponState,
      managerErrors: state.coupon.errors,
      bets: getBets(state),
    };
  },
  {
    setNewBetsFlag,
  }
)(apiForm(ECoupon));



// WEBPACK FOOTER //
// ./app/components/EsportsGame/ECoupon/ECoupon.jsx